import {
  IconButton,
  Modal,
  PlusIcon,
  SingleSelectDropdown,
  Text,
  toast,
} from "@carbon6/c6-frontend-design";
import { useId, useState, type ReactNode } from "react";
import type { SharedWorkspace } from "@/api/trainingWorkspace";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { HatModalHeader, HatTextModal } from "@/components/modal";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { CreateSharedWorkspaceModal } from "./CreateSharedWorkspaceModal";
import { scheduleTimeLabel } from "./scheduleFormat";
import { useSharedWorkspaces } from "./useSharedWorkspaces";
import styles from "./SharedWorkspaceModal.module.css";

export type SharedWorkspaceModalProps = {
  onClose: () => void;
};

/**
 * 그룹 공용 Workspace 관리. Schedule 페이지의 admin 전용 모달이다.
 *
 * 공용 등록(`TrainingWorkspaceGroup`)은 선점자 없이 research 그룹 멤버 모두에게 열린 workspace 다.
 * Schedule 은 이 workspace 에서 만든다. 이 화면에는 다음을 함께 둔다:
 *   - 현황(등록 목록)
 *   - 등록(미선점 후보 선택 또는 새 Workspace 생성)
 *   - 해제
 * 후보는 sync-status 의 unclaimed 다(Team 에 연결된 그룹 아래·미선점·미공용). registry 가 모르는
 * 그룹의 workspace 는 여기 오지 않는다.
 *
 * 서버 상태와 쓰기는 `useSharedWorkspaces` 가 가진다. 여기 남는 상태는 화면 상태뿐이다:
 * 고른 후보, 열린 확인·생성 모달, 생성 폼 입력.
 */
export function SharedWorkspaceModal({ onClose }: SharedWorkspaceModalProps): ReactNode {
  const titleId = useId();
  const {
    shared,
    candidateOptions,
    groupOptions,
    loading,
    loadError,
    candidateError,
    groupError,
    busy,
    defaultGroupId,
    teamNameOf,
    register,
    remove,
    createAndRegister,
  } = useSharedWorkspaces();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<SharedWorkspace | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createGroupId, setCreateGroupId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");

  function openCreate(): void {
    if (busy || groupOptions.length === 0) return;
    setCreateGroupId(defaultGroupId);
    setCreateOpen(true);
  }

  function closeCreate(): void {
    if (busy) return;
    setCreateOpen(false);
    setNewName("");
    setCreateGroupId(null);
  }

  async function handleRegister(): Promise<void> {
    if (selectedId === null || busy) return;
    const result = await register(selectedId);
    if (result.ok) {
      setSelectedId(null);
    } else {
      toast.error(result.message);
    }
  }

  async function handleRemove(target: SharedWorkspace): Promise<void> {
    const result = await remove(target);
    if (result.ok) {
      setRemoveTarget(null);
    } else {
      toast.error(result.message);
    }
  }

  async function handleCreateAndRegister(): Promise<void> {
    const name = newName.trim();
    if (busy || createGroupId === null || name === "") return;
    const result = await createAndRegister(createGroupId, name);
    if (result.ok) {
      setCreateOpen(false);
      setNewName("");
      setCreateGroupId(null);
      return;
    }
    if (result.orphan === null) {
      toast.error(result.message);
      return;
    }
    // 생성은 성공하고 등록만 실패했다. 훅이 후보에 추가했으므로 그것을 선택한 상태로 [등록]을 이어서 하게 한다.
    setSelectedId(result.orphan.id);
    setCreateOpen(false);
    setNewName("");
    setCreateGroupId(null);
    toast.error(
      `Workspace "${result.orphan.name}" 은 만들어졌지만 공용 등록에 실패했습니다. 후보에 올려 두었으니 [등록]을 다시 눌러 주세요. ${result.message}`,
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      closeOnBackdropClick={!busy && !createOpen && removeTarget === null}
      closeOnEscape={!busy && !createOpen && removeTarget === null}
      aria-labelledby={titleId}
    >
      <HatModalHeader
        title="그룹 공용 Workspace 관리"
        titleId={titleId}
        close={{ disabled: busy }}
      />
      <div className={styles.body}>
        <Text variant={hatcheryTextModes["body-normal"]} className={styles.intro}>
          여기 등록한 Workspace에서 Schedule이 실행됩니다. research 그룹 멤버 전원이 이 Workspace의
          Session을 볼 수 있습니다.
        </Text>
        {loadError !== null ? (
          <EmptyState tone="error">{loadError}</EmptyState>
        ) : loading ? (
          <LoadingState>공용 Workspace를 불러오고 있습니다.</LoadingState>
        ) : (
          <>
            <section className={styles.section} aria-label="공용 Workspace 등록">
              <h3 className={styles.sectionTitle}>등록</h3>
              {candidateError !== null ? (
                <Text
                  variant={hatcheryTextModes["body-normal"]}
                  className={styles.error}
                  role="alert"
                >
                  후보를 불러오지 못했습니다. {candidateError}
                </Text>
              ) : null}
              <div className={styles.selectionRow}>
                <SingleSelectDropdown
                  mode={hatcheryDropdownModes.default}
                  className={styles.workspaceSelect}
                  label="Workspace"
                  value={selectedId}
                  options={candidateOptions}
                  placeholder="등록할 Workspace를 고르세요."
                  emptyMessage="등록할 수 있는 Workspace가 없습니다."
                  noResultsMessage="검색 결과가 없습니다."
                  searchPlaceholder="Workspace를 검색해 보세요."
                  searchAction={
                    <IconButton
                      text="새 Workspace 만들기"
                      variant="surface"
                      size="sm"
                      disabled={busy || groupOptions.length === 0}
                      onClick={openCreate}
                    >
                      <PlusIcon />
                    </IconButton>
                  }
                  searchable
                  menuWidth="min(32rem, calc(100vw - 2rem))"
                  disabled={busy || candidateError !== null}
                  onValueChange={setSelectedId}
                />
                <IconButton
                  text="등록"
                  variant="solid"
                  disabled={busy || selectedId === null}
                  onClick={() => void handleRegister()}
                />
              </div>
              <p className={styles.caption}>
                목록에는 Team에 연결된 그룹의 미선점 Workspace만 나옵니다. 없으면 새 Workspace를
                만들어 등록해 주세요.
              </p>
              {groupError !== null ? (
                <Text
                  variant={hatcheryTextModes["body-normal"]}
                  className={styles.error}
                  role="alert"
                >
                  Team 목록을 불러오지 못했습니다. {groupError}
                </Text>
              ) : null}
            </section>
            <section className={styles.section} aria-label="공용 Workspace 현황">
              <h3 className={styles.sectionTitle}>등록 현황</h3>
              {shared.length === 0 ? (
                <EmptyState size="sm">아직 등록된 공용 Workspace가 없습니다.</EmptyState>
              ) : (
                <ul className={styles.items}>
                  {shared.map((row) => (
                    <li key={row.training_workspace_id} className={styles.item}>
                      <span className={styles.itemMain}>
                        <span className={styles.itemName} title={row.workspace_name}>
                          {row.workspace_name}
                        </span>
                        <span className={styles.itemMeta}>
                          Team {teamNameOf(row.training_group_id)} · 등록{" "}
                          {scheduleTimeLabel(row.created_at)}
                        </span>
                      </span>
                      <IconButton
                        text="해제"
                        variant="surface"
                        size="sm"
                        tone="danger"
                        disabled={busy}
                        onClick={() => setRemoveTarget(row)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
      {/* 해제는 되돌릴 수 있지만 research 멤버의 접근이 바로 끊긴다. 그래서 한 번 확인한다. */}
      <HatTextModal
        open={removeTarget !== null}
        label="공용 등록 해제"
        eyebrow={removeTarget?.workspace_name}
        caption="해제해도 이미 만든 Schedule은 계속 실행됩니다. 다만 research 멤버가 이 Workspace의 Session에 닿지 못하고, Schedule을 고치려면 다시 등록해야 합니다."
        okText="해제"
        error
        busy={busy}
        onOkPress={() => {
          if (removeTarget !== null) {
            void handleRemove(removeTarget);
          }
        }}
        onCancelPress={() => setRemoveTarget(null)}
      />
      {createOpen ? (
        <CreateSharedWorkspaceModal
          busy={busy}
          groupId={createGroupId}
          groupOptions={groupOptions}
          name={newName}
          onClose={closeCreate}
          onCreate={() => void handleCreateAndRegister()}
          onGroupChange={setCreateGroupId}
          onNameChange={setNewName}
        />
      ) : null}
    </Modal>
  );
}
